import { camelizeKeys } from 'humps';

// Variants. A single render can have multiple variants.
// Currently this is draft or beauty render. Each variant
// has its own status, type, hotspots, and image URL.
class Variant {
  static defaultProps = {
    uuid: null, // render UUID from API
    type: null, // one of: draft | beauty
    status: null, // one of: created | rendering | finished | failed
    hotspots: [], // list of (x, y, productId, hotspotUuid)
    imageUrl: null, // full URL to render, only applies when status=finished
    thumbnailUrl: null // full URL to thumbnail, only applies when status=finished
  };

  static STATUS_FINISHED = 'finished';
  static STATUS_FAILED = 'failed';

  constructor (overrides) {
    this.fields = { ...Variant.defaultProps, ...overrides };
  }

  makeCopy (overrides) {
    return new Variant({ ...this.fields, ...overrides });
  }

  uuid () { return this.fields.uuid; }
  type () { return this.fields.type; }
  hotspots () { return this.fields.hotspots; }
  imageUrl () { return this.fields.imageUrl; }
  thumbnailUrl () { return this.fields.thumbnailUrl; }

  isFinished () { return this.fields.status === Variant.STATUS_FINISHED; }
  isFailed () { return this.fields.status === Variant.STATUS_FAILED; }
  setFailed () {
    return this.makeCopy({status: Variant.STATUS_FAILED});
  }
}

class Render {
  static defaultProps = {
    uid: null, // a unique ID for this render: <revision_uuid>-<camera_name>
    parentRevisionUuid: null, // the parent revision UUID
    cameraName: '', // the camera name
    variants: {} // Map of variant type to variant.
  };

  static TYPE_DRAFT = 'draft';
  static TYPE_LIVE_SWAP = 'liveSwap';
  static TYPE_BEAUTY = 'beauty';

  // Renders are given a unique ID of: <revision_uuid>-<camera_name>
  static createRenderUid ({ revisionUuid, cameraName }) {
    return `${revisionUuid}-${cameraName}`;
  }

  // Converts warehouse response to our internal model.
  static createRenderFromWarehouseRendering ({ revisionUuid, cameraName, whRender }) {
    const whVariants = camelizeKeys(whRender);

    let variants = {};
    Object.keys(whVariants).forEach(variantType => {
      const whVariant = whVariants[variantType];
      variants[variantType] = Render.makeVariant(variantType, whVariant);
    });

    return new Render({
      uid: Render.createRenderUid({ revisionUuid, cameraName }),
      parentRevisionUuid: revisionUuid,
      cameraName,
      variants
    });
  }

  static makeVariant (variantType, whVariant) {
    return new Variant({
      uuid: whVariant.id,
      type: variantType,
      status: whVariant.status,
      hotspots: whVariant.hotspots,
      imageUrl: whVariant.imagefile,
      thumbnailUrl: whVariant.thumbnailfile
    });
  }

  /** Creates a new RenderV2 instance with defaults and optional overrides. */
  constructor (overrides) {
    this.fields = { ...Render.defaultProps, ...overrides };
  }

  /** Creates a copied RenderV2 instance with optional overrides. */
  makeCopy (overrides) {
    return new Render({ ...this.fields, ...overrides });
  }

  /** Creates a copied RenderV2 instance with updated draft variant. */
  makeCopyWithUpdatedDraftVariant (render) {
    const variants = {
      ...this.fields.variants,
      [Render.TYPE_DRAFT]: Render.makeVariant(Render.TYPE_DRAFT, render)
    };

    return this.makeCopy({ variants });
  }

  /** Creates a copied RenderV2 instance with updated draft variant. */
  makeCopyWithUpdatedBeautyVariant (render) {
    const variants = {
      ...this.fields.variants,
      [Render.TYPE_BEAUTY]: Render.makeVariant(Render.TYPE_BEAUTY, render)
    };

    return this.makeCopy({ variants });
  }

  // =======
  // GETTERS
  // =======
  /** Returns the id for this render. */
  uid () { return this.fields.uid; }

  /** Returns the parent revision uuid for this render. */
  parentRevisionUuid () { return this.fields.parentRevisionUuid; }

  /** Returns the camera name for this render. */
  cameraName () { return this.fields.cameraName; }

  /**
   * Returns the draft render variant if existing.
   * Otherwise, returns null.
   */
  draftVariant () {
    return this.fields.variants[Render.TYPE_DRAFT] ||
      this.fields.variants[Render.TYPE_LIVE_SWAP] ||
      null;
  }

  /**
   * Returns the beauty render variant if exists.
   * Otherwise, returns null.
   */
  beautyVariant () {
    return this.fields.variants[Render.TYPE_BEAUTY] || null;
  }

  variantFinishedOrFailed (type) {
    let variant;
    if (type === Render.TYPE_BEAUTY) {
      variant = this.beautyVariant();
    } else if ([Render.TYPE_LIVE_SWAP, Render.TYPE_DRAFT].includes(type)) {
      variant = this.draftVariant();
    }

    return variant && (variant.isFinished() || variant.isFailed());
  }

  setFailed () {
    let copy = this.makeCopy({status: Variant.STATUS_FAILED});

    if (copy.fields.variants[Render.TYPE_BEAUTY]) { copy.fields.variants[Render.TYPE_BEAUTY] = copy.fields.variants[Render.TYPE_BEAUTY].setFailed(); }
    if (copy.fields.variants[Render.TYPE_DRAFT]) { copy.fields.variants[Render.TYPE_DRAFT] = copy.fields.variants[Render.TYPE_DRAFT].setFailed(); }

    return copy;
  }
}

export default Render;
